import React, { FunctionComponent, useState } from 'react';
import { Field } from 'react-final-form';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import {
  composeValidators,
  digitsOnly,
  emailValidation,
  fileValidation,
  requiredValidation,
  onlyLetters,
  pastDateOnly,
  validateIdCard,
} from 'helpers/validations';
import { getDocLabel } from 'helpers/form';

import MultiStepForm from 'components/commons/MultiStepForm';
import Input from 'components/inputs/Input';
import SelectInput from 'components/inputs/SelectInput';
import FieldRequired from 'components/commons/FieldRequired';
import LocationSelector from 'components/commons/LocationSelector';
import ProcedureStartStep from 'components/commons/ProcedureStartStep';

import { ViewContainer, ViewContent, Title } from 'styles/view';
import { FormRowContainer } from 'styles/form';
import ProcedureReviewStep from 'components/commons/ProcedureReviewStep';
import { IReviewSection } from 'components/commons/ProcedureReviewStep/types';

import { IDivorcesFormData, IDivorcesFormProps } from './types';
import './i18n';

const DivorcesForm: FunctionComponent<IDivorcesFormProps> = (props: IDivorcesFormProps) => {
  const { files, idCardTypes, initialValues, isEditing, onSubmit } = props;

  const [formValues, setFormValues] = useState<IDivorcesFormData>(initialValues);
  const [showStartStep, setShowStartStep] = useState<boolean>(!isEditing);

  const tabs = [
    i18next.t<string>('DIVORCES_FORM:MARRIAGE'),
    i18next.t<string>('DIVORCES_FORM:HUSBAND'),
    i18next.t<string>('DIVORCES_FORM:WIFE'),
    i18next.t<string>('DIVORCES_FORM:COMMENTS'),
  ];

  const today = new Date().toISOString().split('T')[0];

  const requiredFilesDescription = [
    // PESTAÑA: MATRIMONIO (Datos de origen)
    `[OBLIGATORIO] ${i18next.t<string>('DIVORCES_FORM:KETUBA_FILE')}: ${i18next.t<string>(
      'PROCEDURES:KETUBA_DIV_DESC',
      'Libreta de matrimonio religioso (Ketubá) original.',
    )}`,
    `[OBLIGATORIO] ${i18next.t<string>(
      'DIVORCES_FORM:MARRIAGE_CERTIFICATE_FILE',
    )}: ${i18next.t<string>(
      'PROCEDURES:MARR_CERT_DESC',
      'Certificado o acta de matrimonio civil.',
    )}`,

    // PESTAÑA: CÓNYUGES (Husband & Wife)
    `[OBLIGATORIO] ${i18next.t<string>('DIVORCES_FORM:ID_CARD_FILE')}: ${i18next.t<string>(
      'PROCEDURES:ID_COUPLE_DESC',
      'DNI (frente y dorso) de ambos cónyuges.',
    )}`,

    // OPCIONALES / CONDICIONALES
    `[OPCIONAL] ${i18next.t<string>('DIVORCES_FORM:DIVORCE_CERTIFICATE_FILE')}: ${i18next.t<string>(
      'PROCEDURES:DIV_CERT_DESC',
      'Sentencia de divorcio civil previa (si existiera).',
    )}`,
    `[OPCIONAL] ${i18next.t<string>('DIVORCES_FORM:SPOUSE_LINEAGE_DOCS')}: ${i18next.t<string>(
      'PROCEDURES:LINEAGE_DIV_DESC',
      'Ketubá o actas de matrimonio de los cónyuges (en caso de requerir validación extra).',
    )}`,
  ];

  const reviewSections: IReviewSection[] = [
    {
      title: i18next.t('DIVORCES_FORM:MARRIAGE_INFORMATION'),
      fields: [
        { label: i18next.t('DIVORCES_FORM:WEDDING_TEMPLE'), value: formValues.weddingTemple },
        { label: i18next.t('DIVORCES_FORM:WEDDING_DATE'), value: formValues.weddingDate },
      ],
    },
    ...(['husband', 'wife'] as const).map((side) => ({
      title: i18next.t(`DIVORCES_FORM:${side.toUpperCase()}_INFORMATION`),
      fields: [
        { label: i18next.t('DIVORCES_FORM:LAST_NAME'), value: formValues[side]?.lastName },
        { label: i18next.t('DIVORCES_FORM:FIRST_NAME'), value: formValues[side]?.firstName },
        { label: i18next.t('DIVORCES_FORM:ID_CARD'), value: formValues[side]?.idCard },
        { label: i18next.t('DIVORCES_FORM:ID_CARD_TYPE'), value: formValues[side]?.idCardType },
        { label: i18next.t('DIVORCES_FORM:ADDRESS'), value: formValues[side]?.address },
        { label: i18next.t('DIVORCES_FORM:APARTMENT'), value: formValues[side]?.apartment },
        { label: i18next.t('DIVORCES_FORM:COUNTRY', 'País'), value: formValues[side]?.country },
        { label: i18next.t('DIVORCES_FORM:STATE', 'Provincia'), value: formValues[side]?.province },
        {
          label: i18next.t('DIVORCES_FORM:LOCALITY', 'Localidad'),
          value: formValues[side]?.locality,
        },
        {
          label: i18next.t('DIVORCES_FORM:PHONE'),
          value: formValues[side]?.areaCode
            ? `${formValues[side]?.areaCode} ${formValues[side]?.phone}`
            : formValues[side]?.phone,
        },
        { label: i18next.t('DIVORCES_FORM:EMAIL'), value: formValues[side]?.email },
      ],
    })),
    {
      title: i18next.t('DIVORCES_FORM:COMMENTS'),
      fields: [{ label: i18next.t('DIVORCES_FORM:COMMENTS'), value: formValues.comments }],
    },
  ];

  if (showStartStep) {
    return (
      <ViewContainer auth>
        <ProcedureStartStep
          procedureName={i18next.t<string>('DIVORCES_FORM:TITLE')}
          requiredFiles={requiredFilesDescription}
          onStart={() => setShowStartStep(false)}
        />
      </ViewContainer>
    );
  }

  return (
    <ViewContainer auth data-testid="divorce-form">
      <Title>{i18next.t<string>('DIVORCES_FORM:TITLE')}</Title>
      <ViewContent auth>
        <MultiStepForm<IDivorcesFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          onValuesChange={setFormValues}
          tabs={tabs}
          tabsEnabled={isEditing}
        >
          <MultiStepForm.Page key="divorce-page">
            <Typography variant="h6">
              {i18next.t<string>('DIVORCES_FORM:MARRIAGE_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DIVORCES_FORM:WEDDING_TEMPLE')}
                name="weddingTemple"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('DIVORCES_FORM:WEDDING_DATE')}
                name="weddingDate"
                render={Input}
                type="date"
                max={today}
                validate={composeValidators(requiredValidation, pastDateOnly)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DIVORCES_FORM:KETUBA_FILE')}
                name="ketubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('DIVORCES_FORM:KETUBA_FILE'),
                    value: files?.ketubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('DIVORCES_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="marriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('DIVORCES_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.marriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <Field
              label={i18next.t<string>('DIVORCES_FORM:DIVORCE_CERTIFICATE_FILE')}
              name="divorceCertificateFile"
              previewFiles={[
                {
                  label: i18next.t<string>('DIVORCES_FORM:DIVORCE_CERTIFICATE_FILE'),
                  value: files?.divorceCertificateFile,
                },
              ]}
              render={Input}
              type="file"
              validate={fileValidation}
            />
          </MultiStepForm.Page>
          {(['husband', 'wife'] as const).map((value) => (
            <MultiStepForm.Page key={`${value}-page`}>
              <Typography variant="h6">
                {i18next.t<string>(`DIVORCES_FORM:${value.toUpperCase()}_INFORMATION`)}
              </Typography>
              <br />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('DIVORCES_FORM:LAST_NAME')}
                  name={`${value}.lastName`}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
                <FieldRequired
                  label={i18next.t<string>('DIVORCES_FORM:FIRST_NAME')}
                  name={`${value}.firstName`}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('DIVORCES_FORM:ADDRESS')}
                  name={`${value}.address`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('DIVORCES_FORM:APARTMENT')}
                  name={`${value}.apartment`}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <LocationSelector path={value} values={formValues} />
              <FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('DIVORCES_FORM:AREA_CODE')}
                    maxLength={4}
                    name={`${value}.areaCode`}
                    parse={(v: string) => digitsOnly(v)}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('DIVORCES_FORM:PHONE')}
                    maxLength={8}
                    name={`${value}.phone`}
                    parse={(v: string) => digitsOnly(v)}
                    render={Input}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
              </FormRowContainer>
              <Field
                label={i18next.t<string>('DIVORCES_FORM:EMAIL')}
                name={`${value}.email`}
                render={Input}
                type="email"
                validate={emailValidation}
              />
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:ID_CARD_TYPE')}
                  name={`${value}.idCardType`}
                  options={idCardTypes}
                  render={SelectInput}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={getDocLabel(`${value}.idCard`, formValues, idCardTypes)}
                  name={`${value}.idCard`}
                  render={Input}
                  validate={validateIdCard(idCardTypes)}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('DIVORCES_FORM:ID_CARD_FILE')}
                  name={`${value}.idCardFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('DIVORCES_FORM:ID_CARD_FILE'),
                      value: files?.[`${value}IdCardFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
                <FieldRequired
                  label={i18next.t<string>('DIVORCES_FORM:ID_CARD_BACK_FILE')}
                  name={`${value}.idCardBackFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('DIVORCES_FORM:ID_CARD_BACK_FILE'),
                      value: files?.[`${value}IdCardBackFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:KETUBA_FILE')}
                  name={`${value}.ketubaFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('DIVORCES_FORM:KETUBA_FILE'),
                      value: files?.[`${value}KetubaFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={fileValidation}
                />
                <Field
                  label={i18next.t<string>('DIVORCES_FORM:MARRIAGE_CERTIFICATE_FILE')}
                  name={`${value}.marriageCertificateFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('DIVORCES_FORM:MARRIAGE_CERTIFICATE_FILE'),
                      value: files?.[`${value}MarriageCertificateFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={fileValidation}
                />
              </FormRowContainer>
            </MultiStepForm.Page>
          ))}
          <MultiStepForm.Page key="comments-page">
            <Field
              label={i18next.t<string>('DIVORCES_FORM:COMMENTS')}
              name="comments"
              parse={(value) => value}
              render={Input}
              type="textarea"
            />
          </MultiStepForm.Page>
          {!isEditing && (
            <MultiStepForm.Page key="review-page">
              <ProcedureReviewStep sections={reviewSections} />
            </MultiStepForm.Page>
          )}
        </MultiStepForm>
      </ViewContent>
    </ViewContainer>
  );
};

export default DivorcesForm;
